/** @jsxImportSource react */
import { compareNumber } from "lossless-json"
import { exactText, isRecord } from "./json"
import { message } from "./messages"

// Independent defensive rendering bound; metric source populations have their own server limits.
const MAX_BUCKETS = 1024
const VISIBLE_BUCKETS = 10
const NUMBER_TOKEN = /^(-?)(0|[1-9]\d*)(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/

interface Magnitude {
  readonly leading: number
  readonly power: number
}

interface Bucket {
  readonly group: string | null
  readonly value: string
  readonly population: string
  readonly currency: string | null
  readonly magnitude: Magnitude
  readonly negative: boolean
}

function numberToken(value: unknown): string | undefined {
  if (typeof value === "number") {
    if (!Number.isFinite(value) || (Number.isInteger(value) && !Number.isSafeInteger(value))) return undefined
    return String(value)
  }
  return exactText(value)
}

/**
 * Only the leading twelve digits enter a bounded floating-point approximation, exclusively for
 * decoration. Ranking and display use original tokens; business values are never totalled.
 */
function magnitude(token: string): Magnitude | undefined {
  if (token.length > 128) return undefined
  const match = NUMBER_TOKEN.exec(token)
  if (!match) return undefined
  const whole = match[2] ?? ""
  const fraction = match[3] ?? ""
  const exponent = Number(match[4] ?? "0")
  if (!Number.isSafeInteger(exponent) || Math.abs(exponent) > 308) return undefined
  const digits = `${whole}${fraction}`.replace(/^0+/, "")
  if (digits.length === 0) return { leading: 0, power: 0 }
  const power = digits.length - fraction.length + exponent - 1
  if (Math.abs(power) > 308) return undefined
  const leadingDigits = digits.slice(0, 12)
  return { leading: Number(leadingDigits) / 10 ** (leadingDigits.length - 1), power }
}

function readBuckets(value: unknown): readonly Bucket[] | undefined {
  if (!Array.isArray(value) || value.length > MAX_BUCKETS) return undefined
  const buckets: Bucket[] = []
  for (const row of value) {
    if (!isRecord(row) || Object.keys(row).length !== 4
      || !(row.group === null || (typeof row.group === "string" && row.group.length <= 512))
      || !(row.currency === null || (typeof row.currency === "string" && row.currency.length > 0 && row.currency.length <= 32))) return undefined
    const token = numberToken(row.value)
    const size = token === undefined ? undefined : magnitude(token)
    const population = numberToken(row.population)
    if (token === undefined || !size || population === undefined || population.length > 128 || !/^(0|[1-9]\d*)$/.test(population)) return undefined
    buckets.push({ group: row.group, value: token, population, currency: row.currency, magnitude: size, negative: token.startsWith("-") && size.leading !== 0 })
  }
  return buckets
}

function larger(left: Magnitude, right: Magnitude): Magnitude {
  if (left.leading === 0) return right
  if (right.leading === 0) return left
  return left.power > right.power || (left.power === right.power && left.leading > right.leading) ? left : right
}

function barWidth(value: Magnitude, maximum: Magnitude): number {
  if (value.leading === 0 || maximum.leading === 0) return 0
  // Scale relative magnitudes before conversion; 1e308 and 1e-308 never overflow a JS number.
  return Math.min(100, Math.max(0, (value.leading / maximum.leading) * 10 ** (value.power - maximum.power) * 100))
}

/** Rank original number tokens only. The dependency comparator needs explicit zero handling:
 * its normalized zero exponent otherwise puts zero above positive fractions.
 */
function descending(left: Bucket, right: Bucket): number {
  if (left.magnitude.leading === 0) return right.magnitude.leading === 0 ? 0 : right.negative ? -1 : 1
  if (right.magnitude.leading === 0) return left.negative ? 1 : -1
  return compareNumber(right.value, left.value)
}

/** Display only accepted bucket values; masking takes precedence over inspecting the payload. */
export function MetricBuckets({ value, label, locale, masked = false }: { value: unknown; label: string; locale: string; masked?: boolean }) {
  if (masked) return <span className="text-xs text-muted-foreground">{message(locale, "metric.restricted")}</span>
  if (value === null || value === undefined) return <span className="text-xs text-muted-foreground">{message(locale, "metric.noResult")}</span>
  const buckets = readBuckets(value)
  if (!buckets) return <span role="status" className="text-xs text-muted-foreground">{message(locale, "metric.invalid")}</span>
  if (buckets.length === 0) return <span className="text-xs text-muted-foreground">{message(locale, "metric.empty")}</span>
  // Keep currency sections in first-seen order; no cross-currency ranking or conversion occurs.
  const currencies = new Map<string | null, Bucket[]>()
  for (const bucket of buckets) {
    const group = currencies.get(bucket.currency)
    if (group) group.push(bucket)
    else currencies.set(bucket.currency, [bucket])
  }
  return (
    <div className="min-w-0 space-y-5">
      {[...currencies].map(([currency, group]) => {
        const currencyLabel = currency ?? message(locale, "metric.noCurrency")
        const maximum = group.reduce<Magnitude>((largest, bucket) => larger(largest, bucket.magnitude), { leading: 0, power: 0 })
        // Stable sort retains source order for equal exact values and leaves accepted input intact.
        const ranked = [...group].sort(descending)
        return (
          <div key={currency ?? ""} className="min-w-0 space-y-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2 text-2xs text-muted-foreground">
              {currencies.size > 1 ? <span dir="auto" className="font-medium text-foreground">{currencyLabel}</span> : null}
              <span>{message(locale, "metric.partial", { shown: Math.min(group.length, VISIBLE_BUCKETS), total: group.length })}</span>
            </div>
            <ol aria-label={currencies.size > 1 ? message(locale, "metric.currencyLabel", { label, currency: currencyLabel }) : label} className="m-0 grid list-none gap-3 p-0">
              {ranked.slice(0, VISIBLE_BUCKETS).map((bucket, index) => (
                <li key={index} className="min-w-0 space-y-1.5">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                    <span dir="auto" className="min-w-0 break-words text-xs text-foreground">{bucket.group ?? message(locale, "metric.noGroup")}</span>
                    <span dir="ltr" className="max-w-full break-all font-mono text-sm tabular-nums text-foreground">
                      {bucket.value}{bucket.currency ? <span className="ms-1.5 font-sans text-2xs text-muted-foreground">{bucket.currency}</span> : null}
                    </span>
                  </div>
                  <div aria-hidden="true" className="h-1.5 overflow-hidden rounded-sm bg-raised">
                    <div className={bucket.negative ? "h-full rounded-sm bg-muted-foreground" : "h-full rounded-sm bg-primary/75"} style={{ width: `${barWidth(bucket.magnitude, maximum)}%`, minWidth: bucket.magnitude.leading === 0 ? 0 : "2px" }} />
                  </div>
                  <p className="m-0 text-2xs text-subtle-foreground">{message(locale, "metric.population", { population: bucket.population })}</p>
                </li>
              ))}
            </ol>
          </div>
        )
      })}
      <p className="m-0 border-t border-border-subtle pt-2 text-2xs leading-relaxed text-subtle-foreground">
        {message(locale, "metric.order")}{" "}{message(locale, "metric.scale")}
      </p>
    </div>
  )
}
